import { useState } from 'react';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import './Login.css';
import logo from '../images/logo.svg';

interface LoginProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
  onClearSession?: () => void;
}

const Login = ({ onLogin, onClearSession }: LoginProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await onLogin(username, password);
      if (!success) {
        setError('Invalid username or password');
      }
    } catch (_err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Branding Panel */}
        <div className="login-brand-panel">
          <div className="brand-content">
            <div className="brand-logo">
              <img src={`${import.meta.env.BASE_URL}favicon.ico`} alt="Nomi" className="brand-icon" />
              <span className="brand-name">NOMI</span>
            </div>
            <h1 className="brand-headline">AI-Powered Data Intelligence</h1>
            <p className="brand-description">
              Transform your data workflows with intelligent analysis,
              automated pipelines, and natural language queries.
            </p>
          </div>

          {/* Decorative Elements */}
          <div className="brand-decoration">
            <div className="deco-circle deco-circle-1"></div>
            <div className="deco-circle deco-circle-2"></div>
            <div className="deco-line deco-line-1"></div>
            <div className="deco-line deco-line-2"></div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="login-form-panel">
          <div className="form-content">
            <div className="form-header">
              <h2>Welcome Back</h2>
              <p>Sign in to continue to your workspace</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <div className="input-icon">
                  <Mail size={18} />
                </div>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username or email"
                  required
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>

              <div className="input-group">
                <div className="input-icon">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="login-button"
                disabled={isLoading || !username || !password}
              >
                {isLoading ? (
                  <span className="loading-text">Signing in...</span>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {onClearSession && (
              <div className="alternative-action">
                <button
                  type="button"
                  className="text-button"
                  onClick={onClearSession}
                >
                  Use a different account
                </button>
              </div>
            )}

            <div className="login-footer">
              <span>Powered by</span>
              <img src={logo} alt="NomnomData" className="footer-logo" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
